import { PostType } from '@tribeplatform/gql-client/types'

import { findPostType } from '../CMS/utils/postType.js'

import { PostComposerButton } from './@types/index.js'

interface CanCreateAnyPostProps {
  buttons?: PostComposerButton[]
  defaultSpaceId?: string
  networkId?: string
  postTypes?: PostType[]
}

interface CanUseComposerButtonProps {
  button?: Pick<PostComposerButton, 'spaceId' | 'postTypeId'>
  defaultSpaceId?: string
  networkId?: string
  postTypes?: PostType[]
}

const hasAvailableSpace = (postType: PostType, spaceId: string) =>
  postType.authMemberProps?.availableSpaces?.some(
    space => space?.id === spaceId,
  )

const canUseComposerButton = ({
  button,
  defaultSpaceId,
  networkId,
  postTypes = [],
}: CanUseComposerButtonProps) => {
  const effectiveSpaceId = button?.spaceId || defaultSpaceId
  const postTypeId = button?.postTypeId

  if (!effectiveSpaceId && !postTypeId) {
    return true
  }

  if (postTypeId) {
    const postType = findPostType({
      networkId: networkId ?? '',
      id: postTypeId,
      postTypes,
    })

    if (!postType) {
      return false
    }

    if (effectiveSpaceId) {
      return hasAvailableSpace(postType, effectiveSpaceId)
    }

    return postType.authMemberProps?.availableSpaces?.length > 0
  }

  if (effectiveSpaceId) {
    return postTypes.some(postType =>
      hasAvailableSpace(postType, effectiveSpaceId),
    )
  }

  return true
}

export const getVisibleComposerButtons = ({
  buttons = [],
  defaultSpaceId,
  networkId,
  postTypes,
}: CanCreateAnyPostProps) =>
  buttons.filter(button =>
    canUseComposerButton({ button, defaultSpaceId, networkId, postTypes }),
  )

export const canCreateAnyPost = ({
  buttons = [],
  defaultSpaceId,
  networkId,
  postTypes,
}: CanCreateAnyPostProps) => {
  if (!buttons.length) {
    return canUseComposerButton({
      button: {},
      defaultSpaceId,
      networkId,
      postTypes,
    })
  }

  return (
    getVisibleComposerButtons({
      buttons,
      defaultSpaceId,
      networkId,
      postTypes,
    }).length > 0
  )
}
